import React, { useState, useEffect, useMemo } from "react";
import { database } from "../../firebase";
import { ref, onValue, update, remove } from "firebase/database";
import RequireAdmin from "../../hoc/RequireAdmin";
import {
  FaUserPlus,
  FaUserEdit,
  FaTimes,
  FaTrashAlt,
  FaExclamationTriangle,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaGlobe,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaUserShield,
  FaUserTag,
} from "react-icons/fa";

const AddAdmin = () => {
  const [users, setUsers] = useState([]); // 전체 사용자 리스트
  const [selectedUsers, setSelectedUsers] = useState([]); // 선택된 사용자 UID
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // 삭제 확인 모달
  const [searchTerm, setSearchTerm] = useState(""); // 검색어
  const [roleFilter, setRoleFilter] = useState("all"); // 역할 필터 (all, admin, user)
  const [domainFilter, setDomainFilter] = useState("all"); // 도메인 필터 (all, verified, unverified)
  const [currentPage, setCurrentPage] = useState(1); // 현재 페이지
  const itemsPerPage = 10; // 페이지당 항목 수

  // 인증된 도메인 목록
  const verifiedDomains = useMemo(() => [
    "gmail.com", "naver.com", "daum.net", "kakao.com", "hotmail.com", 
    "outlook.com", "yahoo.com", "workway.com", "company.com", "team.com",
    "co.kr", "com", "net", "org", "io", "me", "info", "biz", "co"
  ], []);

  // 사용자 목록 가져오기
  useEffect(() => {
    const usersRef = ref(database, "users");
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const userList = Object.entries(data).map(([uid, userInfo]) => ({
          uid,
          ...userInfo,
        }));
        setUsers(userList);
      }
    });
    return () => unsubscribe();
  }, []);

  // 도메인 인증 상태 확인 함수
  const isDomainVerified = (email) => {
    if (!email) return false;
    const domain = email.split("@")[1];
    if (!domain) return false;

    if (verifiedDomains.includes(domain)) return true;
    const domainParts = domain.split(".");
    if (domainParts.length > 4) return false;

    const tld = domainParts[domainParts.length - 1];
    const secondLevel = domainParts.length > 1 ? domainParts[domainParts.length - 2] + "." + tld : null;

    return verifiedDomains.includes(tld) || (secondLevel && verifiedDomains.includes(secondLevel));
  };

  // 필터링된 사용자 리스트
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch = 
        (user.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.displayName || "").toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      
      const isVerified = isDomainVerified(user.email);
      const matchesDomain = 
        domainFilter === "all" || 
        (domainFilter === "verified" && isVerified) || 
        (domainFilter === "unverified" && !isVerified);

      return matchesSearch && matchesRole && matchesDomain;
    });
  }, [users, searchTerm, roleFilter, domainFilter, verifiedDomains]);

  // 페이지네이션 처리
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const currentUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(1); // 필터 변경 시 첫 페이지로 이동
  }, [searchTerm, roleFilter, domainFilter]);

  const handleCheckboxChange = (uid) => {
    setSelectedUsers((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  // 권한 일괄 변경 함수
  const handleUpdateRoles = (newRole) => {
    if (selectedUsers.length === 0) {
      alert("사용자를 선택해주세요!");
      return;
    }

    const updates = {};
    selectedUsers.forEach((uid) => {
      updates[`users/${uid}/role`] = newRole;
    });

    update(ref(database), updates)
      .then(() => {
        alert(`${selectedUsers.length}명의 권한이 ${newRole === "admin" ? "관리자" : "일반 사용자"}로 변경되었습니다.`);
        setSelectedUsers([]);
      })
      .catch((error) => {
        console.error("권한 변경 중 오류:", error);
        alert("권한 변경 중 오류가 발생했습니다.");
      });
  };

  const handleDeleteUsers = () => {
    const deletePromises = selectedUsers.map((uid) => remove(ref(database, `users/${uid}`)));
    Promise.all(deletePromises)
      .then(() => {
        alert(`${selectedUsers.length}명의 사용자가 삭제되었습니다.`);
        setSelectedUsers([]);
        setShowDeleteConfirm(false);
      })
      .catch((error) => {
        console.error("사용자 삭제 중 오류:", error);
        alert("사용자 삭제 중 오류가 발생했습니다.");
        setShowDeleteConfirm(false);
      });
  };

  return (
    <div className="w-full p-4 lg:p-8 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-black text-gray-900 tracking-tight flex items-center">
              <FaUserShield className="mr-3 text-indigo-600" /> 사용자 및 권한 관리
            </h2>
            <p className="text-gray-500 mt-1">시스템 사용자의 권한을 설정하고 계정을 관리합니다.</p>
          </div>
          
          <div className="flex items-center gap-2 bg-indigo-50 px-4 py-2 rounded-2xl border border-indigo-100">
            <FaGlobe className="text-indigo-400" />
            <span className="text-xs font-bold text-indigo-700 uppercase tracking-widest">Verified Domains Active</span>
          </div>
        </div>

        {/* 필터 및 검색 바 */}
        <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100 mb-8 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative col-span-1 md:col-span-2">
              <input 
                type="text"
                placeholder="이름 또는 이메일로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium"
              />
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>

            <div className="relative">
              <select 
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none appearance-none font-medium text-gray-700"
              >
                <option value="all">전체 권한</option>
                <option value="admin">관리자</option>
                <option value="user">일반 사용자</option>
                <option value="customer">고객</option>
              </select>
              <FaUserTag className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select 
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none appearance-none font-medium text-gray-700"
              >
                <option value="all">전체 도메인</option>
                <option value="verified">인증됨</option>
                <option value="unverified">미인증</option>
              </select>
              <FaFilter className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* 액션 버튼 바 */}
        {selectedUsers.length > 0 && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-8 py-4 rounded-3xl shadow-2xl flex items-center gap-6 animate-bounce-in border border-gray-700">
            <span className="text-sm font-black whitespace-nowrap">
              <span className="text-indigo-400">{selectedUsers.length}명</span> 선택됨
            </span>
            <div className="h-6 w-px bg-gray-700 mx-2"></div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => handleUpdateRoles("admin")}
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center"
              >
                <FaUserPlus className="mr-2" /> 관리자 승격
              </button>
              <button 
                onClick={() => handleUpdateRoles("customer")}
                className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center"
              >
                <FaUserTag className="mr-2" /> 고객 지정
              </button>
              <button 
                onClick={() => handleUpdateRoles("user")}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center"
              >
                <FaUserEdit className="mr-2" /> 일반 변경
              </button>
              <button 
                onClick={() => setShowDeleteConfirm(true)}
                className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center"
              >
                <FaTrashAlt className="mr-2" /> 계정 삭제
              </button>
              <button 
                onClick={() => setSelectedUsers([])}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        )}

        {/* 사용자 테이블 */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-6 py-3">
                    <input 
                      type="checkbox" 
                      onChange={(e) => {
                        if (e.target.checked) setSelectedUsers(currentUsers.map(u => u.uid));
                        else setSelectedUsers([]);
                      }}
                      className="w-5 h-5 rounded-lg border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-xs font-black text-gray-400 uppercase tracking-widest">사용자 정보</th>
                  <th className="px-6 py-3 text-xs font-black text-gray-400 uppercase tracking-widest text-center">도메인 인증</th>
                  <th className="px-6 py-3 text-xs font-black text-gray-400 uppercase tracking-widest text-center">현재 권한</th>
                  <th className="px-6 py-3 text-xs font-black text-gray-400 uppercase tracking-widest text-right">가입일</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-20 text-center text-gray-400 font-bold">검색 결과가 없습니다.</td>
                  </tr>
                ) : (
                  currentUsers.map((user) => {
                    const isVerified = isDomainVerified(user.email);
                    return (
                      <tr key={user.uid} className={`hover:bg-indigo-50/30 transition-colors ${selectedUsers.includes(user.uid) ? 'bg-indigo-50/50' : ''}`}>
                        <td className="px-6 py-3">
                          <input 
                            type="checkbox"
                            checked={selectedUsers.includes(user.uid)}
                            onChange={() => handleCheckboxChange(user.uid)}
                            className="w-5 h-5 rounded-lg border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-black mr-3">
                              {(user.displayName || user.name || "U")[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="font-bold text-gray-900">{user.displayName || user.name || "이름 없음"}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-center">
                          {isVerified ? (
                            <span className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 text-[10px] font-black uppercase rounded-full">
                              <FaCheckCircle className="mr-1" /> VERIFIED
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 bg-red-50 text-red-700 text-[10px] font-black uppercase rounded-full">
                              <FaTimesCircle className="mr-1" /> UNVERIFIED
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-center">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${
                            user.role === "admin" 
                              ? "bg-purple-100 text-purple-700 border border-purple-200" 
                              : user.role === "customer"
                              ? "bg-green-100 text-green-700 border border-green-200"
                              : "bg-blue-100 text-blue-700 border border-blue-200"
                          }`}>
                            {user.role === "admin" ? "ADMIN" : user.role === "customer" ? "CUSTOMER" : "USER"}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right font-mono text-[10px] text-gray-400">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 transition-all shadow-sm"
            >
              <FaChevronLeft className="text-gray-600" />
            </button>
            
            <div className="flex items-center gap-2">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${
                    currentPage === i + 1 
                    ? "bg-indigo-600 text-white shadow-lg shadow-indigo-100" 
                    : "bg-white text-gray-400 hover:text-gray-900 border border-gray-100"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-3 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 transition-all shadow-sm"
            >
              <FaChevronRight className="text-gray-600" />
            </button>
          </div>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-sm w-full border border-gray-100 animate-scale-in">
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaExclamationTriangle className="text-red-500 text-4xl" />
            </div>
            <h3 className="text-2xl font-black text-center mb-2 text-gray-900">정말로 삭제하시겠습니까?</h3>
            <p className="text-gray-500 text-center mb-8 font-medium">선택된 {selectedUsers.length}명의 계정이 시스템에서 완전히 삭제되며, 이 작업은 취소할 수 없습니다.</p>
            <div className="flex gap-3">
              <button 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-4 rounded-2xl font-black transition-all active:scale-95"
                onClick={handleDeleteUsers}
              >
                삭제 진행
              </button>
              <button 
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 py-4 rounded-2xl font-black transition-all"
                onClick={() => setShowDeleteConfirm(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequireAdmin(AddAdmin);
